import { SectionGroups } from "@/components/marketing/SectionLayout";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { breadcrumbJsonLd } from "@/content/schema";
import type { GlossaryTerm } from "@/content/glossary";
import { GLOSSARY } from "@/content/glossary";
import { SITE, abs } from "@/content/siteConfig";
import { OnThisPage } from "@/components/marketing/OnThisPage";
import { slugifyHeading } from "@/lib/articleEnhance";
import { RecentWork } from "@/components/marketing/RecentWork";

const SET_URL = abs("/glossary");

export const Route = createFileRoute("/glossary/$slug")({
  loader: async ({ params }) => {
    const { getTerm, glossaryTitle } = await import("@/content/glossary");
    const entry = getTerm(params.slug);
    if (!entry) throw notFound();
    return { entry, title: entry.seoTitle ?? glossaryTitle(entry.term) };
  },
  head: ({ loaderData }) => {
    const entry = loaderData?.entry;
    if (!entry) return {};
    const url = abs(`/glossary/${entry.slug}`);
    const title = loaderData.title;
    const description = entry.meta ?? entry.definition;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: entry.shareImage ? abs(entry.shareImage.src) : abs(SITE.ogImagePath) },
        ...(entry.shareImage
          ? [
              { property: "og:image:width", content: String(entry.shareImage.width ?? 1600) },
              { property: "og:image:height", content: String(entry.shareImage.height ?? 900) },
              { property: "og:image:alt", content: entry.shareImage.alt },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: entry.shareImage ? abs(entry.shareImage.src) : abs(SITE.ogImagePath) },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Glossary", path: "/glossary" },
            { name: entry.term, path: `/glossary/${entry.slug}` },
          ]),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: title,
            alternateName: entry.term,
            description: entry.slug === "local-services-ads" ? description : entry.definition,
            url,
            inDefinedTermSet: SET_URL,
            ...(entry.shareImage
              ? {
                  image: {
                    "@type": "ImageObject",
                    url: abs(entry.shareImage.src),
                    width: entry.shareImage.width ?? 1600,
                    height: entry.shareImage.height ?? 900,
                  },
                }
              : {}),
            ...(entry.updatedAt ? { dateModified: entry.updatedAt } : {}),
          }),
        },
        ...(entry.faqs && entry.faqs.length > 0
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: entry.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  component: GlossaryTermPage,
});

const H2: React.CSSProperties = {
  fontSize: 22,
  letterSpacing: "-0.01em",
  color: "#002FA7",
  marginTop: 48,
};

const BODY: React.CSSProperties = {
  fontSize: 17,
  lineHeight: 1.68,
  color: "rgba(0,0,0,0.76)",
  marginTop: 14,
};

/** Renders inline [label](/path) links inside glossary prose. */
function inline(text: string) {
  const parts: React.ReactNode[] = [];
  const re = /\[([^\]]+)\]\((\/[^)]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <Link
        key={`${m.index}-${m[2]}`}
        to={m[2] as never}
        style={{ color: "#002FA7", textDecoration: "underline", textUnderlineOffset: 3 }}
      >
        {m[1]}
      </Link>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function GlossaryTermPage() {
  const { entry } = Route.useLoaderData() as { entry: GlossaryTerm };
  const related = entry.related
    .map((s: string) => GLOSSARY.find((t: GlossaryTerm) => t.slug === s))
    .filter((t): t is GlossaryTerm => Boolean(t));
  const toc = [
    "Why it matters",
    ...(entry.whatPeopleGetWrong ? ["What people get wrong"] : []),
    ...(entry.sections ?? []).map((s) => s.heading),
    ...(entry.faqs && entry.faqs.length > 0 ? ["Frequently asked"] : []),
    "Related terms",
  ].map((text) => ({ id: slugifyHeading(text), text }));

  return (
    <SiteShell>
      <div className="wrap" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <SectionGroups className="glossary-reading reading-layout section-groups" style={{ maxWidth: 720, margin: "0 auto" }}>
          <Link
            to="/glossary"
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(0,47,167,0.6)",
            }}
          >
            ← Glossary
          </Link>

          <h1
            className="prose-lead mt-6"
            style={{ fontSize: "clamp(30px, 4.6vw, 48px)", lineHeight: 1.08, letterSpacing: "-0.02em" }}
          >
            {entry.term}
          </h1>

          <p
            className="mt-6"
            style={{
              fontSize: 21,
              lineHeight: 1.5,
              color: "#002FA7",
              borderLeft: "2px solid rgba(0,47,167,0.35)",
              paddingLeft: 20,
            }}
          >
            {entry.slug === "local-services-ads" ? inline(entry.definition) : entry.definition}
          </p>

          <OnThisPage items={toc} />

          <h2 id={slugifyHeading("Why it matters")} style={H2}>Why it matters</h2>
          <p style={BODY}>{entry.whyItMatters}</p>

          {entry.whatPeopleGetWrong && (
            <>
              <h2 id={slugifyHeading("What people get wrong")} style={H2}>What people get wrong</h2>
              <p style={BODY}>{entry.whatPeopleGetWrong}</p>
            </>
          )}

          {entry.sections?.map((section) => (
            <div key={section.heading}>
              <h2 id={slugifyHeading(section.heading)} style={H2}>{section.heading}</h2>
              {section.paragraphs.map((para, i) => (
                <p key={i} style={BODY}>
                  {inline(para)}
                </p>
              ))}
              {section.image ? (
                <figure style={{ margin: "1.4em 0" }}>
                  <a href={section.image.src} target="_blank" rel="noopener" style={{ display: "block" }}>
                    <img
                      src={section.image.src}
                      alt={section.image.alt}
                      width={section.image.width ?? 1600}
                      height={section.image.height ?? 900}
                      loading={section.image.eager ? "eager" : "lazy"}
                      decoding="async"
                      style={{ display: "block", width: "100%", height: "auto" }}
                    />
                  </a>
                  <figcaption
                    className="mono"
                    style={{ fontSize: 12, letterSpacing: "0.02em", color: "rgba(0,0,0,0.55)", marginTop: 10 }}
                  >
                    {section.image.caption}
                  </figcaption>
                </figure>
              ) : null}
              {section.table ? (
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full border-collapse text-left" style={{ minWidth: 680 }}>
                    <thead>
                      <tr>
                        {section.table.headers.map((header) => (
                          <th
                            key={header}
                            className="mono"
                            style={{ borderBottom: "1px solid rgba(0,47,167,0.35)", padding: "12px 14px", fontSize: 11, color: "#002FA7", textTransform: "uppercase" }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row) => (
                        <tr key={row[0]}>
                          {row.map((cell, index) => (
                            <td
                              key={`${row[0]}-${index}`}
                              style={{ borderBottom: "1px solid rgba(0,47,167,0.14)", padding: "14px", fontSize: 15, lineHeight: 1.5, color: index === 0 ? "#002FA7" : "rgba(0,0,0,0.76)", verticalAlign: "top" }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ))}

          {entry.faqs && entry.faqs.length > 0 ? (
            <>
              <h2 id={slugifyHeading("Frequently asked")} style={H2}>Frequently asked</h2>
              {entry.faqs.map((f) => (
                <div key={f.q} className="faq-item">
                  <h3
                    style={{
                      fontSize: 22,
                      lineHeight: 1.35,
                      letterSpacing: "-0.01em",
                      color: "#002FA7",
                      marginTop: 30,
                    }}
                  >
                    {f.q}
                  </h3>
                  <p style={BODY}>{f.a}</p>
                </div>
              ))}
            </>
          ) : null}

          <h2 id={slugifyHeading("Related terms")} style={H2}>Related terms</h2>
          <ul className="mt-4 m-0 list-none p-0">
            {related.map((t: GlossaryTerm) => (
              <li key={t.slug} style={{ padding: "8px 0" }}>
                <Link
                  to="/glossary/$slug"
                  params={{ slug: t.slug }}
                  style={{ fontSize: 16.5, color: "#002FA7", textDecoration: "underline" }}
                >
                  {t.term}
                </Link>
              </li>
            ))}
          </ul>
        </SectionGroups>
      </div>
      {entry.slug === "local-services-ads" ? <RecentWork /> : null}
    </SiteShell>
  );
}
